import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, DataTable, Drawer, EmptyState, Inline, Link, Panel, Skeleton, Stack, t, Text } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { shortDate } from '../lib/format';
import { FULL_HEIGHT } from '../lib/tables';

type Running = fleetState.components['schemas']['Running'];
export type Install = NonNullable<Running['wordpress']>[number];
type Php = NonNullable<Running['php']>[number];
type Vulnerability = fleetState.components['schemas']['Vulnerability'];
type Severity = NonNullable<Vulnerability['severity']>;

const SEVERITY_TONES = { critical: 'neg', high: 'neg', medium: 'warn', low: 'neutral' } as const;
const ORDER: (Severity | null)[] = ['critical', 'high', 'medium', 'low', null];

const worst = (list: Vulnerability[]) => [...list].sort((a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity))[0]?.severity ?? null;

/** One row per component with a known vulnerability, the worst first. */
function findings(install: Install) {
  const rows: { id: string; kind: string; name: string; version: string | null; v: Vulnerability }[] = [];
  install.core.forEach((v, i) => rows.push({ id: `core:${i}`, kind: t('appSecurity.kind.core'), name: 'WordPress', version: install.version, v }));
  for (const [kind, list] of [['plugin', install.plugins], ['theme', install.themes]] as const) {
    for (const e of list) e.vulnerabilities.forEach((v, i) => rows.push({ id: `${kind}:${e.slug}:${i}`, kind: t(`appSecurity.kind.${kind}`), name: e.slug, version: e.version, v }));
  }
  return rows.sort((a, b) => ORDER.indexOf(a.v.severity) - ORDER.indexOf(b.v.severity));
}

function InstallState({ install }: { install: Install }) {
  const all = [...install.core, ...install.plugins.flatMap(p => p.vulnerabilities), ...install.themes.flatMap(p => p.vulnerabilities)];
  if (all.length) {
    const severity = worst(all);
    return <Badge tone={severity === 'critical' || severity === 'high' ? 'neg' : 'warn'} size="sm">{t(severity === 'critical' ? 'appSecurity.state.critical' : 'appSecurity.state.vulnerable', { n: all.length })}</Badge>;
  }
  return install.checked
    ? <Badge tone="pos" size="sm">{t('appSecurity.state.none')}</Badge>
    : <Badge tone="neutral" size="sm">{t('appSecurity.state.unchecked')}</Badge>;
}

function PhpState({ php }: { php: Php }) {
  if (php.endOfLife) return <Badge tone="neg" size="sm">{t('appSecurity.php.endOfLife', { date: shortDate(php.endOfLife) })}</Badge>;
  if (php.securityRelease) return <Badge tone="neg" size="sm">{t('appSecurity.php.securityRelease', { version: php.securityRelease })}</Badge>;
  if (php.followedBy === 'distribution') return <Badge tone="neutral" size="sm">{t('appSecurity.php.byDistribution')}</Badge>;
  return php.checked ? <Badge tone="pos" size="sm">{t('appSecurity.php.upToDate')}</Badge> : <Badge tone="neutral" size="sm">{t('appSecurity.state.unchecked')}</Badge>;
}

/**
 * D39, « Logiciels » tab: the WordPress installs and the PHP
 * versions the agent reads on the server, with the vulnerabilities known
 * against them (WPVulnerability, php.net, endoflife.date). Information only:
 * applying the fixes comes later (lot 3).
 */
export function AppSecurityPanels({ server, only }: {
  server: string;
  /** D47: within an « Applications » block, the installs of that deployment, and PHP or not. */
  only?: { wordpress: (install: Install) => boolean; php: boolean };
}) {
  const [open, setOpen] = useState<Install | null>(null);
  // The same query as the other « Ce qui tourne » tabs: one reading for all.
  const running = useQuery({
    queryKey: ['server-running', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
    refetchInterval: 60_000,
  });
  const r = running.data;
  if (running.isPending) return <Panel title={t('appSecurity.wordpress.title')}><Skeleton lines={3} /></Panel>;
  if (!r || r.restricted || !r.installed) return null;
  if (r.wordpress === null && r.php === null) {
    if (only) return null;
    return (
      <Panel title={t('appSecurity.wordpress.title')}>
        <EmptyState framed={false} icon="update" title={t('appSecurity.notRead.title')}>{t('appSecurity.notRead.body')}</EmptyState>
      </Panel>
    );
  }
  // A server with no WordPress and no PHP shows neither panel.
  const wordpress = (r.wordpress ?? []).filter(i => !only || only.wordpress(i));
  const php = !only || only.php ? r.php ?? [] : [];
  if (!wordpress.length && !php.length) return null;
  const rows = open ? findings(open) : [];
  return (
    <Stack gap={16}>
      {wordpress.length > 0 && <Panel title={t('appSecurity.wordpress.title')} subtitle={t('appSecurity.wordpress.subtitle')} flush>
        <DataTable
          bordered={false}
          height={FULL_HEIGHT}
          label={t('appSecurity.wordpress.title')}
          rows={wordpress}
          rowKey="path"
          toolbar={false}
          density="compact"
          onRowClick={(i: Install) => setOpen(i)}
          activeRowKey={open?.path}
          columns={[
            { key: 'path', label: t('appSecurity.wordpress.path'), mono: true, strong: true, grow: true, width: 240 },
            { key: 'state', label: t('appSecurity.wordpress.state'), width: 220, render: (i: Install) => <InstallState install={i} /> },
            { key: 'version', label: t('appSecurity.wordpress.version'), mono: true, width: 90 },
            { key: 'plugins', label: t('appSecurity.wordpress.plugins'), type: 'number', width: 90, accessor: (i: Install) => i.plugins.length },
            { key: 'themes', label: t('appSecurity.wordpress.themes'), type: 'number', width: 90, accessor: (i: Install) => i.themes.length },
            // The sites the install serves, on the same line as the rest.
            { key: 'domains', label: t('running.sites.domains'), mono: true, width: 220, accessor: (i: Install) => i.domains.join(', ') || '—' },
          ]}
        />
      </Panel>}
      {php.length > 0 && <Panel title={t('appSecurity.php.title')} subtitle={t('appSecurity.php.subtitle')} flush>
        <DataTable
          bordered={false}
          height={FULL_HEIGHT}
          label={t('appSecurity.php.title')}
          rows={php}
          rowKey="branch"
          toolbar={false}
          density="compact"
          columns={[
            { key: 'branch', label: t('appSecurity.php.version'), width: 130, mono: true, accessor: (p: Php) => p.version ?? p.branch },
            // D58: « par défaut » and « FPM » in their own column, never badges after the version.
            { key: 'usage', label: t('appSecurity.php.usage'), width: 150, sortable: false,
              accessor: (p: Php) => [p.default ? t('appSecurity.php.default') : '', p.fpm ? t('appSecurity.php.fpm') : ''].filter(Boolean).join(' · '),
              render: (p: Php) => (p.default || p.fpm ? (
                <Inline gap={4} align="center" wrap={false}>
                  {p.default && <Badge tone="neutral" size="sm">{t('appSecurity.php.default')}</Badge>}
                  {p.fpm && <Badge tone="neutral" size="sm">{t('appSecurity.php.fpm')}</Badge>}
                </Inline>
              ) : null) },
            { key: 'state', label: t('appSecurity.php.state'), width: 300, render: (p: Php) => <PhpState php={p} /> },
            { key: 'package', label: t('appSecurity.php.package'), mono: true, grow: true, width: 200, render: (p: Php) => p.package ?? '—' },
            { key: 'origin', label: t('appSecurity.php.origin'), width: 130, render: (p: Php) => t(`appSecurity.php.origins.${p.origin}`) },
          ]}
        />
      </Panel>}

      <Drawer
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        eyebrow={t('appSecurity.drawer.eyebrow')}
        title={open?.path ?? ''}
        subtitle={open ? t('appSecurity.drawer.subtitle', { version: open.version, plugins: open.plugins.length, themes: open.themes.length }) : undefined}
        size="xl"
        footer={<Button variant="secondary" onClick={() => setOpen(null)}>{t('appSecurity.drawer.close')}</Button>}
      >
        {open && (
          <Stack gap={16}>
            <DataTable
              height={FULL_HEIGHT}
              label={t('appSecurity.drawer.findings')}
              rows={rows}
              rowKey="id"
              toolbar={false}
              density="compact"
              columns={[
                { key: 'name', label: t('appSecurity.drawer.component'), width: 160, mono: true },
                // D58: the component's kind in its own column, never a badge after its name.
                { key: 'kind', label: t('appSecurity.drawer.kind'), width: 90, render: (f: (typeof rows)[number]) => <Badge tone="neutral" size="sm">{f.kind}</Badge> },
                { key: 'version', label: t('appSecurity.wordpress.version'), mono: true, width: 90, accessor: (f: (typeof rows)[number]) => f.version ?? '—' },
                { key: 'severity', label: t('appSecurity.drawer.severity'), width: 130, render: (f: (typeof rows)[number]) => (f.v.severity
                  ? <Badge tone={SEVERITY_TONES[f.v.severity]} size="sm">{`${t(`appSecurity.severity.${f.v.severity}`)}${f.v.score !== null ? ` · ${f.v.score}` : ''}`}</Badge>
                  : <Text variant="body-sm" tone="mute">—</Text>) },
                // The title, then its reference (a link when the source gives one), on one line.
                { key: 'title', label: t('appSecurity.drawer.vulnerability'), grow: true, width: 200, render: (f: (typeof rows)[number]) => (
                  <Inline gap={8} align="center" wrap={false}>
                    <Text variant="body-sm" truncate>{f.v.title}</Text>
                    {f.v.id && (f.v.link ? <Link href={f.v.link} external>{f.v.id}</Link> : <Text variant="caption">{f.v.id}</Text>)}
                  </Inline>
                ) },
                { key: 'fixedIn', label: t('appSecurity.drawer.fixedIn'), width: 130, render: (f: (typeof rows)[number]) => (f.v.unfixed
                  ? <Badge tone="neg" size="sm">{t('appSecurity.drawer.unfixed')}</Badge>
                  : <Text variant="code">{f.v.fixedIn ?? '—'}</Text>) },
              ]}
              empty={<EmptyState framed={false} icon="verified" title={open.checked ? t('appSecurity.drawer.none') : t('appSecurity.state.unchecked')} />}
            />
            <Text variant="body-sm" tone="mute">{t('appSecurity.drawer.source')}</Text>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
